<template>
  <div
    id="header-bottom__search"
    ref="header-search"
    :class="[cssClasses, { 'header-bottom__search--is-expanded': isSearchOpen }]"
    :hidden="!isVisible">
    <div class="instant-search">
      <div class="instant-search__container">
        <label id="header-bottom-label" class="sr-only" for="header-bottom-input">{{ tStatic('headerSearchAria') }}</label>
        <input
          id="header-bottom-input"
          ref="search"
          v-model="searchQuery"
          type="input"
          class="instant-search__input"
          :placeholder="tStatic('searchSitePlaceholder')"
          @focus="onSearchFocus"
          @keydown="onSearchKeyDown">

        <button
          v-if="showClearButton"
          :aria-label="tStatic('clearHeaderSearchAria')"
          type="button"
          class="instant-search__clear"
          @click="clearSearchQuery">
          <oie-use-svg svg="close"></oie-use-svg>
        </button>

        <button
          type="button"
          class="instant-search__icon"
          aria-labelledby="header-bottom-label"
          @click="visitSearchResult">
          <oie-use-svg svg="search"></oie-use-svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'
import search from '@/mixins/search'
import { createSearchResultQueryParams } from '@/core/search-service'

export default {
  mixins: [search],
  data: () => ({
    activeSearchResultIndex: -1,
    activeSearchColumn: 0,
    showSearch: true
  }),
  computed: {
    ...mapGetters('ui', ['isSearchOpen']),
    showClearButton() {
      return this.searchQuery.length >= 2
    },
    currentItems() {
      return [[...this.entities.items.data], [...this.entities.categories.data, ...this.entities.models.data]]
    },
    activeSearchItem() {
      return _.get(this.currentItems, [this.activeSearchColumn, this.activeSearchResultIndex])
    },
    isVisible() {
      return this.mq('medium') || this.isSearchOpen
    }
  },
  watch: {
    entities: {
      deep: true,
      handler(entities) {
        this.publish('instantsearch.entities.changed', entities)
      }
    },
    searchQuery: _.debounce(function (query) {
      for (var i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if(_.startsWith(key, 'last_scroll_')) {
          sessionStorage.removeItem(key)
        }
      }
      this.$store.dispatch('ui/setSearchQuery', query)
      const performSearch = query.length > 2
      this.searchType = 'Header search'
      if (performSearch) {
        this.searchAll()
      } else {
        this.clearSearchResults()
      }
      this.updateShowSearch(performSearch)
    }, 500),
    showSearchResults(value) {
      if(!value) {
        this.activeSearchResultIndex = -1
      }
    },
    isSearchOpen() {
      if (this.isSearchOpen) {
        this.$refs.search.focus()
      }
    }
  },
  mounted() {
    this.subscribe('instantsearch.clickoutside.clear.search', () => {
      this.searchQuery = ''
      this.clearSearchResults()
    })
  },
  methods: {
    visitSearchResult() {
      const isLoading = _.some(this.pendingCancellations)
      if(isLoading) {
        return
      }
      const params = createSearchResultQueryParams(this.searchQuery, this.entities)
      window.location = `${this.clientModel.searchResultPageUrl}?${params}`
    },
    focusChanged(e) {
      this.publish('focus.changed', e)
    },
    onSearchKeyDown(e) {
      switch (e.keyCode) {
        case 38:
          if (--this.activeSearchResultIndex < 0) {
            this.activeSearchResultIndex = this.currentItems[this.activeSearchColumn].length - 1
          }
          break
        case 39: case 37:
          this.activeSearchColumn = 1 - this.activeSearchColumn
          break
        case 40:
          if (++this.activeSearchResultIndex === this.currentItems[this.activeSearchColumn].length) {
            this.activeSearchResultIndex = 0
          }
          break
        case 13:
          if(this.activeSearchItem) {
            window.location = this.activeSearchItem.url
          } else {
            this.visitSearchResult()
          }
          break
        case 27:
          this.updateShowSearch(false)
          this.clearSearchQuery()
          break
      }
      this.$store.dispatch('ui/setActiveSearchItem', this.activeSearchItem)
    },
    updateShowSearch(value) {
      this.showSearch = value
      this.publish('search.show', value)
    },
    onSearchFocus() {
      this.updateShowSearch(true)
    },
    clearSearchQuery() {
      this.searchQuery = ''
      this.$refs.search.focus()
      this.clearSearchResults()
    }
  }
}
</script>

<style lang="scss">
.instant-search {
  position: relative;
  width: 100%;
  background-color: color(search-background);
  border-radius: other(border-radius);
  margin-top: measure(half);
  transition: opacity 140ms;

  @include media(medium) {
    margin-top: 0;
  }
}

.instant-search__container {
  display: flex;
  justify-content: flex-end;
}

.instant-search__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0 other(border-radius) other(border-radius) 0;
  background-color: color(gray-4);
  flex-grow: 0;
  flex-shrink: 0;
  flex-basis: 2.5rem;
  margin: 0;
  padding: 0;
  transition: all 200ms;

  > svg {
    width: 1.25rem;
    height: 1.25rem;
    color: color(cart-indicator);
    transition: color 200ms;
  }
}

.instant-search__input {
  @include body-smaller;
  flex-grow: 1;
  display: block;
  border: none;
  height: 2.25rem;
  padding: 0 measure(quarter) 0 measure(half);
  border-radius: other(border-radius) 0 0 other(border-radius);
  min-width: 1px;

  @include media(medium) {
    height: 2.5rem;
  }
}

.instant-search__clear {
  border: none;
  background-color: transparent;
  margin: 0 measure(quarter) 0 0;
  padding: 0;

  > svg {
    width: 1rem;
    height: 1rem;
  }
}
</style>
